import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import VideoModal from "./VideoModal";
import FeatureCalloutsSection from "./landing/FeatureCalloutsSection";
import "./LandingHero.css";

// Pillar copy configuration
const PILLAR_COPY = {
  gamification: {
    eyebrow: "GAMIFICATION",
    heading: "Progress you can actually feel",
    body: "Scout turns your work into real progress. Earn trophies, badges, streaks, and levels that come straight from the assignments you finish and the materials you upload. No fake points. Just real momentum as you grind through the semester.",
    screenshot: "/landing_page_hero/screenshots/Gamification_Screenshot_Gamification.png",
    screenshotAlt: "Gamification dashboard showing trophies, levels, and progress",
    screenshotLabel: "Levels, trophies, and streaks update automatically as you complete real academic work."
  },
  productivity: {
    eyebrow: "PRODUCTIVITY ENGINE",
    heading: "Your whole semester builds itself",
    body: "Upload your syllabi once and Scout handles the rest. Your classes, deadlines, events, and workload spread across the semester so you can plan ahead instead of scrambling. Everything lives in one clean timeline that updates as you move.",
    screenshots: [
      {
        src: "/landing_page_hero/screenshots/Timeline__Screenshot_Productivity_Tool.png",
        alt: "Timeline view showing assignments and deadlines across weeks"
      },
      {
        src: "/landing_page_hero/screenshots/Calendar_Screenshot_Productivity_Tool.png",
        alt: "Calendar view showing daily schedule and events"
      }
    ],
    screenshotLabel: "Your calendar stays synced as your courses evolve."
  },
  aiTools: {
    eyebrow: "AI STUDY TOOLS",
    heading: "Study tools built right into your materials",
    body: "Scout's tools work through your documents, not around them. Generate quizzes, flashcards, summaries, study notes, and key terms straight from your uploads so staying locked in is way easier.",
    screenshot: "/landing_page_hero/screenshots/Scouts Deks__Screenshot_AI_Study_Tools.png",
    screenshotAlt: "Scout's Desk showing AI study tools interface with documents",
    screenshotLabel: "Tools adapt to the documents you upload for each class."
  },
  companion: {
    eyebrow: "COMPANION",
    heading: "A study companion who actually knows your world",
    body: "Scout isn't some generic chatbot. He understands your classes, documents, assignments, and schedule so you can ask real questions about what you're learning and what's coming next. Everything happens in one place, built around you.",
    screenshot: "/landing_page_hero/screenshots/Chat__Screenshot_Companion.png",
    screenshotAlt: "Chat interface showing Scout companion conversation",
    screenshotLabel: "Ask questions across your courses and uploaded materials."
  }
};

// Pillar configuration
const PILLARS = [
  {
    id: "gamification",
    label: "Gamification",
    image: "/landing_page_hero/gamification_pillar_hero.png",
    ariaLabel: "View Gamification details"
  },
  {
    id: "productivity",
    label: "Productivity Engine",
    image: "/landing_page_hero/productivity_engine_pillar_hero.png",
    ariaLabel: "View Productivity Engine details"
  },
  {
    id: "aiTools",
    label: "AI Study Tools",
    image: "/landing_page_hero/aistudytools_hero.png",
    ariaLabel: "View AI Study Tools details"
  },
  {
    id: "companion",
    label: "Companion",
    image: "/landing_page_hero/companion_pillar_hero.png",
    ariaLabel: "View Companion details"
  }
];

// Scout Overview component (default center state)
function ScoutOverview({ withBase }) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const openVideo = () => {
    // Always reset scroll to top so the movie screen is fully in view
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setIsVideoOpen(true);
  };

  const closeVideo = () => setIsVideoOpen(false);

  return (
    <>
      <div className="hero-center-shell">
        <div className="hero-center-card">

          {/* Centered Scout at top */}
          <div className="hero-scout-top">
            <img
              src="/landing_page_hero/Scout_Thumbsup.png"
              alt="Scout the DocuHounds companion"
            />
          </div>

          <h1 className="hero-title">
            One place for everything school.
          </h1>

          <div className="hero-keywords">
            <span>Assignments.</span>
            <span>Notes.</span>
            <span>Deadlines.</span>
            <span>AI study tools.</span>
          </div>

          <div className="hero-subheadline">
            <p className="hero-subheadline-primary">
              Whether you're in high school or college, DocuHounds keeps everything organized so you can spend less time managing school and more time succeeding.
            </p>
          </div>

          <div className="hero-quick-points">
            <div className="hero-point">✓ Never miss another assignment</div>
            <div className="hero-point">✓ Turn notes into quizzes, flashcards, podcasts & study guides</div>
            <div className="hero-point">✓ Sync every class across all your devices</div>
          </div>

          <div className="hero-cta-row">
            <Link to="/register" className="btn hero-btn-primary">
              Get Started Free
            </Link>

            <button
              type="button"
              className="btn hero-btn-secondary"
              onClick={openVideo}
            >
              See How It Works
            </button>
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={closeVideo}
        videoUrl="https://www.youtube.com/embed/tkjEytwGtec?autoplay=1"
      />
    </>
  );
}

// Pillar Detail Card component
function PillarDetailCard({ pillar, onBack }) {
  const copy = PILLAR_COPY[pillar];
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  // Special layout for Gamification
  if (pillar === 'gamification') {
    return (
      <div className="pillar-detail-card pillar-detail-gamification">
        <div className="pillar-detail-layout">
          {/* TOP: Screenshot */}
          <div className="pillar-detail-media">
            <div className="pillar-detail-media-frame">
              <img
                src={copy.screenshot}
                alt={copy.screenshotAlt}
              />
            </div>

            <p className="pillar-detail-caption pillar-detail-caption--mobile">
              {copy.screenshotLabel}
            </p>
          </div>

          {/* BOTTOM: Text content */}
          <div className="pillar-detail-text">
            <p className="pillar-detail-kicker">{copy.eyebrow}</p>

            <h2 className="pillar-detail-title">{copy.heading}</h2>

            <p className="pillar-detail-body">{copy.body}</p>

            <p className="pillar-detail-caption pillar-detail-caption--desktop">
              {copy.screenshotLabel}
            </p>

            <button
              type="button"
              className="pillar-back-btn"
              onClick={onBack}
            >
              ← Back to Scout overview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Productivity pillar - special layout with multiple screenshots and dot navigation
  if (pillar === 'productivity') {
    return (
      <div className="pillar-detail-card pillar-detail-productivity">
        <div className="pillar-detail-layout">
          {/* TOP: Screenshot with dots */}
          <div className="pillar-detail-media">
            <div className="pillar-screenshot-carousel">
              {copy.screenshots.map((screenshot, index) => (
                <div
                  key={index}
                  className={`pillar-detail-media-frame ${activeScreenshot === index ? 'screenshot-active' : 'screenshot-inactive'} ${index === 0 ? 'screenshot-timeline' : 'screenshot-calendar'}`}
                >
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                  />
                </div>
              ))}
            </div>

            {/* Screenshot navigation dots */}
            <div className="screenshot-dots">
              {copy.screenshots.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`screenshot-dot ${index === activeScreenshot ? 'screenshot-dot--active' : ''}`}
                  onClick={() => setActiveScreenshot(index)}
                  aria-label={`View screenshot ${index + 1}`}
                />
              ))}
            </div>

            <p className="pillar-detail-caption pillar-detail-caption--mobile">
              {copy.screenshotLabel}
            </p>
          </div>

          {/* BOTTOM: Text content */}
          <div className="pillar-detail-text">
            <p className="pillar-detail-kicker">{copy.eyebrow}</p>

            <h2 className="pillar-detail-title">{copy.heading}</h2>

            <p className="pillar-detail-body">{copy.body}</p>

            <p className="pillar-detail-caption pillar-detail-caption--desktop">
              {copy.screenshotLabel}
            </p>

            <button
              type="button"
              className="pillar-back-btn"
              onClick={onBack}
            >
              ← Back to Scout overview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // AI Tools and Companion - same layout as Gamification
  return (
    <div className={`pillar-detail-card pillar-detail-${pillar}`}>
      <div className="pillar-detail-layout">
        {/* TOP: Screenshot */}
        <div className="pillar-detail-media">
          <div className="pillar-detail-media-frame">
            <img
              src={copy.screenshot}
              alt={copy.screenshotAlt}
            />
          </div>

          <p className="pillar-detail-caption pillar-detail-caption--mobile">
            {copy.screenshotLabel}
          </p>
        </div>

        {/* BOTTOM: Text content */}
        <div className="pillar-detail-text">
          <p className="pillar-detail-kicker">{copy.eyebrow}</p>

          <h2 className="pillar-detail-title">{copy.heading}</h2>

          <p className="pillar-detail-body">{copy.body}</p>

          <p className="pillar-detail-caption pillar-detail-caption--desktop">
            {copy.screenshotLabel}
          </p>

          <button
            type="button"
            className="pillar-back-btn"
            onClick={onBack}
          >
            ← Back to Scout overview
          </button>
        </div>
      </div>
    </div>
  );
}

const LandingHero = () => {
  const [selectedPillar, setSelectedPillar] = useState(null);
  
  // Runtime base path logic matching LandingPage.js
  const location = useLocation();
  const runtimeBase = (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '')
    ? process.env.PUBLIC_URL
    : (location.pathname.startsWith('/m') ? '/m' : '');
  const withBase = (path) => (runtimeBase ? `${runtimeBase}${path}` : path);

  // Toggle click handler (pillar buttons are hidden on desktop via CSS, but still usable on mobile)
  const handlePillarClick = (id) => {
    setSelectedPillar(current => (current === id ? null : id));
  };

  // Key for center content remounting
  const centerKey = selectedPillar ?? 'scout-overview';

  return (
    <>
      <section 
        className="landing-hero" 
        style={{ backgroundImage: `url("${withBase('/landing_page_hero/docuhoundsbackgroundclassroom.png')}")`}}
      >
        <div className="landing-hero__overlay">
          <div className="hero-outer">
            <div className="landing-hero__content">
              {/* Desktop: center panel only. Pillars hidden via CSS */}
              <div className="hero-grid">
                {/* Pillar buttons - hidden on desktop via CSS */}
                <button
                  type="button"
                  className="pillar-button pillar-button-hidden"
                  aria-hidden="true"
                  onClick={() => handlePillarClick(PILLARS[0].id)}
                >
                  <img
                    src={withBase(PILLARS[0].image)}
                    alt={PILLARS[0].label}
                    className="pillar-image"
                  />
                </button>

                <button
                  type="button"
                  className="pillar-button pillar-button-hidden"
                  aria-hidden="true"
                  onClick={() => handlePillarClick(PILLARS[1].id)}
                >
                  <img
                    src={withBase(PILLARS[1].image)}
                    alt={PILLARS[1].label}
                    className="pillar-image"
                  />
                </button>

                {/* Center content panel - always shows ScoutOverview */}
                <div 
                  className="hero-center-panel"
                  aria-label="Scout overview"
                  key={centerKey}
                >
                  {selectedPillar ? (
                    <PillarDetailCard pillar={selectedPillar} onBack={() => setSelectedPillar(null)} />
                  ) : (
                    <ScoutOverview withBase={withBase} />
                  )}
                </div>

                <button
                  type="button"
                  className="pillar-button pillar-button-hidden"
                  aria-hidden="true"
                  onClick={() => handlePillarClick(PILLARS[2].id)}
                >
                  <img
                    src={withBase(PILLARS[2].image)}
                    alt={PILLARS[2].label}
                    className="pillar-image"
                  />
                </button>

                <button
                  type="button"
                  className="pillar-button pillar-button-hidden"
                  aria-hidden="true"
                  onClick={() => handlePillarClick(PILLARS[3].id)}
                >
                  <img
                    src={withBase(PILLARS[3].image)}
                    alt={PILLARS[3].label}
                    className="pillar-image"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Callouts - Desktop only */}
      <FeatureCalloutsSection />
    </>
  );
};

export default LandingHero;
